import { useMemo } from 'react';
import { pctToStyle } from '../lib/coords';
import { fieldLabelText, isLabelVisible } from '../lib/fieldTypes';
import PdfPage from './PdfPage';

/**
 * The field's caption, drawn immediately to the LEFT of its box: the signer
 * reads what's being asked for, then fills the box beside it. Outside the
 * control, so it never eats into the hit area or the entered value.
 */
const LeftLabel = ({ field }) =>
  isLabelVisible(field) ? (
    <span className="pointer-events-none absolute right-full top-0 mr-1 flex h-full items-center whitespace-nowrap text-[11px] leading-none text-[#334155]">
      {fieldLabelText(field)}
      {field.required && <span aria-hidden>*</span>}
    </span>
  ) : null;

const todayLabel = () => {
  const now = new Date();
  return `${String(now.getMonth() + 1).padStart(2, '0')}/${String(now.getDate()).padStart(2, '0')}/${now.getFullYear()}`;
};

/**
 * The signer's document view: every page rendered by pdf.js with this party's
 * fields overlaid as live inputs at their percentage positions (OtterWaiver's
 * PdfSignatureStep). Signature/initial boxes open the capture modal via
 * `onSignatureClick`; other inputs write into `values` through `onChange`.
 */
export default function SignFieldsOverlay({
  pdf,
  fields,
  values,
  onChange,
  onSignatureClick,
  signer,
  width = 760,
}) {
  const pageNumbers = useMemo(
    () => Array.from({ length: pdf.numPages }, (_, i) => i + 1),
    [pdf],
  );

  const autofillText = (field) => {
    switch (field.kind) {
      case 'name':
        return `${signer.firstName} ${signer.lastName ?? ''}`.trim();
      case 'email':
        return signer.email;
      case 'phone':
        return signer.phone ?? '';
      case 'date_signed':
        return todayLabel();
      default:
        return '';
    }
  };

  const selectRadio = (field) => {
    // One choice per group: setting this option clears its siblings.
    fields
      .filter((f) => f.kind === 'radio' && f.radioGroup === field.radioGroup && f.id !== field.id)
      .forEach((f) => onChange(f.id, ''));
    onChange(field.id, 'true');
  };

  const renderField = (field) => {
    const value = values[field.id] ?? '';
    /** Positioned shell: the label hangs off its left edge, the control fills it. */
    const shell = (children) => (
      <span key={field.id} className="absolute" style={pctToStyle(field)}>
        <LeftLabel field={field} />
        {children}
      </span>
    );
    /** Only marked inline when the left label isn't there to carry it. */
    const inlineRequired = !isLabelVisible(field) && field.required;

    switch (field.kind) {
      case 'signature':
      case 'initial': {
        const isDrawn = value.startsWith('data:image/');
        return shell(
          <button
            type="button"
            onClick={() => onSignatureClick(field)}
            data-signfield={field.id}
            className={`flex h-full w-full items-center justify-center overflow-hidden rounded-sm border text-xs font-medium transition-colors ${
              value
                ? 'border-transparent'
                : 'border-brand-600 bg-brand-600/10 text-brand-600 hover:bg-brand-600/20'
            }`}
          >
            {isDrawn ? (
              <img src={value} alt="Signature" className="max-h-full max-w-full object-contain" />
            ) : value ? (
              <span
                className="truncate px-1 text-lg text-[#1e293b]"
                style={{ fontFamily: "'Dancing Script', cursive" }}
              >
                {value}
              </span>
            ) : (
              <>
                {field.kind === 'initial' ? 'Initials' : 'Sign'}
                {inlineRequired && <span aria-hidden>*</span>}
              </>
            )}
          </button>,
        );
      }
      case 'text':
      case 'address':
        return shell(
          <input
            type="text"
            data-signfield={field.id}
            value={value}
            onChange={(e) => onChange(field.id, e.target.value)}
            placeholder={inlineRequired ? '*' : ''}
            className="h-full w-full rounded-sm border border-brand-600/60 bg-[#fff8e1]/90 px-1 text-[#1e293b] placeholder:text-[#94a3b8] focus:border-brand-600 focus:outline-none"
            style={{ fontSize: `${Math.max(field.fontSize - 2, 8)}px` }}
          />,
        );
      case 'checkbox':
        return shell(
          <label
            data-signfield={field.id}
            className="flex h-full w-full cursor-pointer items-center justify-center"
            title={field.label ?? undefined}
          >
            <input
              type="checkbox"
              checked={value === 'true'}
              onChange={(e) => onChange(field.id, e.target.checked ? 'true' : 'false')}
              className="h-full max-h-5 w-full max-w-5 accent-brand-600"
            />
          </label>,
        );
      case 'radio':
        return shell(
          <label
            data-signfield={field.id}
            className="flex h-full w-full cursor-pointer items-center justify-center"
            title={field.label ?? undefined}
          >
            <input
              type="radio"
              name={`radio-${field.radioGroup ?? field.id}`}
              checked={value === 'true'}
              onChange={() => selectRadio(field)}
              className="h-full max-h-5 w-full max-w-5 accent-brand-600"
            />
          </label>,
        );
      default:
        // Auto-filled identity/date fields — shown, not editable.
        return shell(
          <span
            data-signfield={field.id}
            className="flex h-full w-full items-center overflow-hidden rounded-sm bg-[#64748b]/10 px-1 text-[#334155]"
            style={{ fontSize: `${Math.max(field.fontSize - 2, 8)}px` }}
          >
            <span className="truncate">{autofillText(field)}</span>
          </span>,
        );
    }
  };

  return (
    <div className="space-y-6">
      {pageNumbers.map((pageNumber) => (
        <PdfPage key={pageNumber} pdf={pdf} pageNumber={pageNumber} width={width}>
          {fields.filter((f) => f.pageNumber === pageNumber).map(renderField)}
        </PdfPage>
      ))}
    </div>
  );
}
